import {createFileRoute, redirect, useParams} from "@tanstack/react-router";

import {config} from "@/config/config.ts";
import ClubLandingPage from "../clubs/ClubLandingPage.tsx";
import services from "../services";

/**
 * The old root address of a club page: scorepal.co/{club-slug}. Every club now lives at
 * /club/{slug} (clubs/clubUrl.ts); this keeps links shared before the move working.
 *
 * This is the lowest-priority catch-all root route — every static route
 * (/pricing, /login, /live, …) and prefixed dynamic route (/e/…, /r/…) is
 * matched first. The slug reserved-word list on the backend prevents a club
 * from ever claiming one of those paths, and ReservedSlugsTest fails the build
 * if a new root route is added without reserving its segment.
 *
 * No club is canonical here any more: every one is handed over to /club/{slug} below.
 */
export const Route = createFileRoute("/$club_slug")({
    component: RouteComponent,
    // The club is fetched in beforeLoad rather than the loader so the redirect is decided
    // before rendering starts, which is where every other redirect in this app lives
    // (faq.tsx, the live/* legacy prefixes). A loader redirect does also work, but it races
    // the render and TanStack issues it as a 307; deciding here lets us state the 301 we mean.
    //
    // Local dev cannot verify any of this: the dev server answers /faq/ with a 200 where
    // staging answers 301, so response-status changes have to be checked against a deployed
    // environment with curl, not the browser (client-side navigation hides the difference).
    beforeLoad: async ({params}) => {
        let club: any = null;
        try {
            const data = await services.clubs.page(params.club_slug);
            club = data?.club ?? null;
        } catch {
            // Unresolvable slug: fall through and let the page render its not-found state.
        }

        // Hand every club to its canonical prefixed path rather than serving the same page at two
        // URLs. The root path keeps resolving them so any link shared before still lands, it just
        // doesn't stay here. 301: the move is permanent, for venues too.
        if (club) {
            throw redirect({to: "/club/$club_slug/", params: {club_slug: params.club_slug}, statusCode: 301});
        }

        return {club};
    },
    loader: ({context}) => ({club: (context as any).club ?? null}),
    head: ({loaderData, params}) => {
        const club: any = (loaderData as any)?.club;
        const name = club?.name || params.club_slug;
        const location = [club?.city, club?.country_code].filter(Boolean).join(", ");
        const desc = `${name}${location ? ` in ${location}` : ""} on ScorePal — open events, live scores, draws, standings and past champions.`;
        return {
            meta: [
                {title: `${name} | ScorePal`},
                {name: "description", content: desc},
                {property: "og:title", content: `${name} | ScorePal`},
                {property: "og:description", content: desc},
                ...(club?.image_url ? [{property: "og:image", content: club.image_url}] : []),
            ],
            // Every club 301s away in beforeLoad, so only the not-found state renders here, and
            // it has no better address than the URL it was reached on.
            links: [{rel: "canonical", href: `${config.publicUrl}/${params.club_slug}/`}],
        };
    },
});

function RouteComponent() {
    const {club_slug} = useParams({from: "/$club_slug"});
    return <ClubLandingPage slug={club_slug} />;
}
